import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import tailwindClassnames from "../common/TailwindClassnames";
import SingleDatePicker from "../common/SingleDatePicker";
import { useProfile } from "../common/ProfileProvider";
import {
  formatDateAsLocalString,
  parseLocalDateStringToDate,
} from "../common/helpers";

type ReviewDateRangeInputProps = {
  label: string;
  /** Local date string (yyyy-MM-dd), or null when unset. */
  captureTimeFrom: string | null;
  /** Local date string (yyyy-MM-dd), or null when unset. */
  captureTimeTo: string | null;
  /** Called with the new (captureTimeFrom, captureTimeTo) pair whenever either date changes. */
  onChange: (
    captureTimeFrom: string | null,
    captureTimeTo: string | null,
  ) => void;
  /**
   * Called whenever either box's text or accepted date changes, so the filter
   * dialog can refuse Apply while a box shows a date the filter does not hold
   * (AKL-1007). See `dateRangeErrorForApply`.
   */
  onDraftChange?: (draft: DateRangeDraft) => void;
  /**
   * Show the "format" error. It stays hidden while the reviewer is still
   * typing and appears once Apply has been refused; the "order" error is
   * always shown because a complete date is final.
   */
  showFormatError?: boolean;
};

/**
 * Why the date boxes cannot be applied as shown: "order" when a box holds a
 * complete, real date its partner's bound refused (a start after the end, or
 * an end before the start); "format" when a box holds anything else the
 * picker has not accepted (incomplete or impossible); null when every box is
 * empty or shows exactly the accepted date.
 */
export type DateRangeDraftError = "order" | "format" | null;

/** What each box shows, and the accepted dates it was compared with. */
export type DateRangeDraft = {
  startText: string;
  endText: string;
  captureTimeFrom: string | null;
  captureTimeTo: string | null;
};

const COMPLETE_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Whether `text` is a complete, real `yyyy-mm-dd` calendar date (rejects
 * `2015-02-30`). Checked in UTC so the result never depends on the
 * browser's time zone.
 * @param text the typed text
 * @returns true for a real date
 */
const isCompleteDate = (text: string): boolean => {
  const match = COMPLETE_DATE.exec(text);
  if (!match) return false;
  const [year, month, day] = match.slice(1).map(Number);
  const parsed = new Date(0);
  parsed.setUTCFullYear(year, month - 1, day);
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
};

/**
 * Compare what each date box shows with the date the filter actually holds.
 * A box whose text differs from its accepted value would be silently dropped
 * by Apply (AKL-1007), so the filter dialog refuses Apply on any non-null
 * result.
 * @param startText the start box's displayed text
 * @param endText the end box's displayed text
 * @param captureTimeFrom the accepted start (`yyyy-MM-dd`), or null
 * @param captureTimeTo the accepted end (`yyyy-MM-dd`), or null
 * @returns the error, or null when the boxes can be applied as shown
 */
export const dateRangeDraftError = (
  startText: string,
  endText: string,
  captureTimeFrom: string | null,
  captureTimeTo: string | null,
): DateRangeDraftError => {
  // Exact text, no trim: react-datepicker commits nothing for " " or a padded
  // date, so only a truly empty box means the date was cleared (Copilot,
  // PR #821).
  const mismatched = [
    [startText, captureTimeFrom ?? ""],
    [endText, captureTimeTo ?? ""],
  ].filter(([text, accepted]) => text !== "" && text !== accepted);
  if (mismatched.length === 0) return null;
  return mismatched.every(([text]) => isCompleteDate(text))
    ? "order"
    : "format";
};

/**
 * The error Apply should act on, given the last reported `draft` and the
 * dates the filter holds right now. They can differ within one key event:
 * Enter over a highlighted calendar day accepts that day and then applies,
 * before the box's text has caught up with it. A box whose accepted date
 * changed since the report has just been accepted, so it counts as showing
 * that date.
 * @param draft the last reported box texts and the dates they were compared with
 * @param captureTimeFrom the start the filter holds now, or null
 * @param captureTimeTo the end the filter holds now, or null
 * @returns the error, or null when Apply may go ahead
 */
export const dateRangeErrorForApply = (
  draft: DateRangeDraft,
  captureTimeFrom: string | null,
  captureTimeTo: string | null,
): DateRangeDraftError =>
  dateRangeDraftError(
    draft.captureTimeFrom === captureTimeFrom
      ? draft.startText
      : (captureTimeFrom ?? ""),
    draft.captureTimeTo === captureTimeTo
      ? draft.endText
      : (captureTimeTo ?? ""),
    captureTimeFrom,
    captureTimeTo,
  );

// Same look as the shared tableFilterDateInput token, but full-width (instead
// of a fixed w-60) and with room on the right for the calendar glyph — this
// field sits two-up in the modal's grid column, not alone in a toolbar.
const dateInputClassName = tailwindClassnames.tableFilterDateInput.replace(
  "w-60",
  "w-full pr-7",
);

/**
 * Independent start/end date pickers for the review filter modal's date-range
 * filter — as opposed to one combined range control. Both sides render
 * through the shared `SingleDatePicker`, differing only in which boundary
 * they enforce: `minDate`/`maxDate` (not `selectsStart`/`selectsEnd`) keep
 * the end date from landing before the start date and vice versa, without
 * the in-range/selecting-range day highlighting that pairing would also turn
 * on — this modal just needs the boundary enforced, not a range-picking
 * visual. The calendar is pinned to open above the field (`fixedPopperPlacement`)
 * since this control can sit near the bottom of the scrollable modal, where
 * react-datepicker's default flip/shift behavior would otherwise
 * inconsistently open it above or below depending on available space.
 *
 * Those bounds make react-datepicker refuse a typed date on the wrong side
 * of the other box, but the refused text stays on screen. So this control
 * watches each box's text, reports it to the dialog through `onDraftChange`
 * (the dialog refuses Apply on `dateRangeErrorForApply`) and shows why under
 * the fields (AKL-1007).
 */
const ReviewDateRangeInput: React.FC<ReviewDateRangeInputProps> = ({
  label,
  captureTimeFrom,
  captureTimeTo,
  onChange,
  onDraftChange,
  showFormatError = false,
}) => {
  const { t } = useTranslation();
  const [startText, setStartText] = useState(captureTimeFrom ?? "");
  const [endText, setEndText] = useState(captureTimeTo ?? "");
  const handleStartText = useCallback((text: string) => setStartText(text), []);
  const handleEndText = useCallback((text: string) => setEndText(text), []);

  const error = dateRangeDraftError(
    startText,
    endText,
    captureTimeFrom,
    captureTimeTo,
  );

  useEffect(() => {
    onDraftChange?.({ startText, endText, captureTimeFrom, captureTimeTo });
  }, [startText, endText, captureTimeFrom, captureTimeTo, onDraftChange]);

  // A date refused because it was on the wrong side of the other box stays
  // on screen. If that other date then moves or is cleared so the shown
  // date fits, accept it now: react-datepicker never re-offers a refused
  // value when its bound changes, so without this the box would keep a
  // valid-looking date that Apply refuses (Copilot, PR #821).
  //
  // Only the box whose partner just changed is revisited, and only when its
  // own accepted date did not change in the same update. A box that was
  // itself just edited (cleared, say) reports its new text one render late,
  // so reading its text here would re-accept the old date and undo the
  // edit. `onChange` is read through a ref: callers pass an inline function,
  // and depending on it would re-run this on every render.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const prevFromRef = useRef(captureTimeFrom);
  const prevToRef = useRef(captureTimeTo);
  useEffect(() => {
    const fromChanged = prevFromRef.current !== captureTimeFrom;
    const toChanged = prevToRef.current !== captureTimeTo;
    prevFromRef.current = captureTimeFrom;
    prevToRef.current = captureTimeTo;
    const fits = (from: string | null, to: string | null) =>
      from === null || to === null || from <= to;
    if (
      toChanged &&
      !fromChanged &&
      startText !== (captureTimeFrom ?? "") &&
      isCompleteDate(startText) &&
      fits(startText, captureTimeTo)
    ) {
      onChangeRef.current(startText, captureTimeTo);
    } else if (
      fromChanged &&
      !toChanged &&
      endText !== (captureTimeTo ?? "") &&
      isCompleteDate(endText) &&
      fits(captureTimeFrom, endText)
    ) {
      onChangeRef.current(captureTimeFrom, endText);
    }
  }, [startText, endText, captureTimeFrom, captureTimeTo]);

  const message =
    error === "order"
      ? t("The start date must be on or before the end date.")
      : error === "format" && showFormatError
        ? t("Enter a valid date as YYYY-MM-DD.")
        : null;
  const { theme } = useProfile();
  const isDarkMode = theme === "dark";

  const startDate = parseLocalDateStringToDate(captureTimeFrom) ?? null;
  const endDate = parseLocalDateStringToDate(captureTimeTo) ?? null;

  const calendarClassName = `custom-calendar review-date-calendar ${
    isDarkMode ? "dark-mode-calendar" : ""
  }`;

  return (
    <div className="flex flex-col">
      <label className={tailwindClassnames.textInputLabel}>{label}</label>
      <div className="flex flex-row items-center gap-2 w-full">
        <SingleDatePicker
          selected={startDate}
          onChange={(date) =>
            onChange(date ? formatDateAsLocalString(date) : null, captureTimeTo)
          }
          placeholder={t("Start Date")}
          ariaLabel={t("Start Date")}
          maxDate={endDate ?? undefined}
          calendarClassName={calendarClassName}
          inputClassName={dateInputClassName}
          todayButtonLabel={t("Today")}
          showCalendarIcon
          fixedPopperPlacement="top-start"
          onDraftTextChange={handleStartText}
        />
        <span className="text-off-black-light dark:text-greyed-text">—</span>
        <SingleDatePicker
          selected={endDate}
          onChange={(date) =>
            onChange(
              captureTimeFrom,
              date ? formatDateAsLocalString(date) : null,
            )
          }
          placeholder={t("End Date")}
          ariaLabel={t("End Date")}
          minDate={startDate ?? undefined}
          calendarClassName={calendarClassName}
          inputClassName={dateInputClassName}
          todayButtonLabel={t("Today")}
          showCalendarIcon
          fixedPopperPlacement="top-start"
          onDraftTextChange={handleEndText}
        />
      </div>
      {message && (
        <p
          role="alert"
          data-testid="review-date-range-error"
          className="bg-primary-orange-light rounded text-primary-orange-dark text-sm px-3 py-2 my-1"
        >
          {message}
        </p>
      )}
    </div>
  );
};

export default ReviewDateRangeInput;
